import {AttrsSchema, Component} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {InputComponent} from 'common/components/input';

import {ButtonTheme} from '../button/constants';
import {template} from './template';
import css from './index.inline.styl';

type InputGroupState = {
  value: string;
  errors: string[];
};

export type InputGroupAttrs = {
  'no-event-bubble'?: boolean;
  busy?: boolean;
  disabled?: boolean;
  icon?: string;
  label?: string;
  maxlength?: number;
  min?: number;
  placeholder?: string;
  readonly?: boolean;
  theme?: ButtonTheme;
  value?: string;
  'value-type'?: string;
  'value-type-article'?: string;
};

type InputGroupJsxProps = {
  attrs: InputGroupAttrs;
  on?: {
    change?: () => void;
    submit?: (ev: Event) => void;
  };
};

enum Errors {
  MinLength = `minLength`,
  MaxLength = `maxLength`,
  Required = `required`,
}

export class InputGroup extends Component<InputGroupState, InputGroupJsxProps['attrs']> {
  static get attrsSchema(): AttrsSchema<InputGroupJsxProps['attrs']> {
    return {
      ...super.attrsSchema,
      'no-event-bubble': `boolean`,
      busy: `boolean`,
      disabled: `boolean`,
      icon: `string`,
      label: {type: `string`, default: `Save`},
      maxlength: `number`,
      min: `number`,
      placeholder: `string`,
      readonly: `boolean`,
      theme: `string`,
      value: `string`,
      'value-type': {type: `string`, default: `input`},
      'value-type-article': {type: `string`, default: `an`},
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      defaultState: {
        value: ``,
        errors: [],
      } as InputGroupState,
      helpers: {
        getErrorMessage: (): string => {
          const {errors} = this.state;
          const valueType = this.attr(`value-type`);
          const valueTypeArticle = this.attr(`value-type-article`);

          if (errors.includes(Errors.MaxLength)) {
            return `Enter ${valueTypeArticle} ${valueType} with a maximum of ${this.attr(`maxlength`)} characters`;
          }

          if (errors.includes(Errors.Required)) {
            return `Enter ${valueTypeArticle} ${valueType} between ${this.attr(`min`)} and ${this.attr(
              `maxlength`,
            )} characters long`;
          }

          if (errors.includes(Errors.MinLength)) {
            return `Enter ${valueTypeArticle} ${valueType} with a minimum of ${this.attr(`min`)} characters`;
          }

          return ``;
        },
        checkValid: () => {
          const {value} = this.state;
          const maxLength = this.attr(`maxlength`);
          const minLength = this.attr(`min`);

          return {
            [Errors.MaxLength]: !maxLength || value.trim().length <= maxLength,
            [Errors.MinLength]: !minLength || value.trim().length >= minLength,
            [Errors.Required]: Boolean(value.trim()),
          };
        },
        isValid: () => Object.values(this.helpers.checkValid()).every((isValid) => isValid),
        setValue: (ev) => {
          this.update({value: ev.target.value});
          this.dispatchEvent(new Event(`change`));
        },

        submitIfEnter: (ev) => {
          if (ev.keyCode === 13) {
            this.helpers.checkErrors();
            this._submitIfValid();
          }
        },

        // called when the input blurs or `enter`
        checkErrors: () => {
          const errors = Object.entries(this.helpers.checkValid())
            .map(([name, isValid]) => (!isValid ? name : null))
            .filter((x) => x);
          this.update({
            errors,
          });
        },

        // Clear any existing errors and rely solely on real-time submit button enablement for immediate feedback to the user
        handleFocus: () => this.update({errors: []}),
        submit: () => {
          this._submitIfValid();
        },
      },
    };
  }

  get value() {
    return this.state.value;
  }

  set value(value) {
    this.update({value});
  }

  focus() {
    return this.el.querySelector<InputComponent>(`.mp-input-group-input`).focus();
  }

  select() {
    return this.el.querySelector<InputComponent>(`.mp-input-group-input`).select();
  }

  _submitIfValid() {
    if (this.helpers.isValid() && !this.attr(`busy`)) {
      this.dispatchEvent(new Event(`submit`, {bubbles: !this.attr(`no-event-bubble`), composed: true}));
    }
  }

  _updateValueFromAttr() {
    this.update({value: this.attr(`value`)});
  }

  connectedCallback() {
    super.connectedCallback();
    if (this.attr(`value`)) {
      this._updateValueFromAttr();
    }
  }

  attributeChangedCallback(attr: string, oldValue: string, newValue: string) {
    super.attributeChangedCallback(attr, oldValue, newValue);
    if (this.initialized && attr === `value`) {
      this._updateValueFromAttr();
    }
  }
}

export const InputGroupEl = defineAndMakeJsxFactory<InputGroupJsxProps>(`mp-input-group`, InputGroup);
export const InputGroupReact = panel2react<InputGroupJsxProps>(`mp-input-group`, InputGroup);
